// Annotation model (enlarge-preview-annotate, design "Web: annotation model"). Pure functions that turn a
// Normalized_Result into Extracted_Items with their Field_Locations, select a page's Annotations, find
// Unlocated_Items, and build Annotation_Label text and accessible names.
import {
  isNotFound,
  resultSegments,
  type FieldLocation,
  type FieldValue,
  type NormalizedField,
  type NormalizedResult,
  type NormalizedSegment,
  type NormalizedTable,
  type PreviewRow,
  type PreviewView,
  type SetRunSnapshot,
} from '@bda/shared';
import { formatValue, isLow } from '../runs/ResultView';

export interface ExtractedItem {
  /** `${segmentIndex}:${path}`, unique within a result. */
  key: string;
  /** field | group.field | table[i].column | list[i] */
  path: string;
  segmentIndex: number;
  /** Set results: the matched member's name; otherwise null. */
  memberName: string | null;
  /** The value formatted as the Result_View formats it. */
  display: string;
  confidence: number | null;
  notFound: boolean;
  locations: FieldLocation[];
}

export interface AnnotationModel {
  /** Result_View order, `MATCH` segments only. */
  items: ExtractedItem[];
  isSet: boolean;
}

/** One drawn Annotation: one Field_Location of one Extracted_Item. */
export interface Annotation {
  key: string;
  item: ExtractedItem;
  location: FieldLocation;
}

export const UNKNOWN_BLUEPRINT = 'Unknown blueprint';
export const LABEL_VISIBLE_CHARS = 40;
export const UNLOCATED_LIST_MAX = 50;

const isSetResult = (segments: NormalizedSegment[], snapshot?: SetRunSnapshot): boolean =>
  snapshot !== undefined || segments.some((s) => s.matchedMember !== undefined);

function memberNameOf(segment: NormalizedSegment, isSet: boolean, snapshot?: SetRunSnapshot): string | null {
  if (!isSet) return null;
  const m = segment.matchedMember;
  const member = snapshot && m !== null && m !== undefined ? snapshot.members[m] : undefined;
  return member?.name ?? segment.matchedBlueprint?.name ?? UNKNOWN_BLUEPRINT;
}

type ItemBase = Pick<ExtractedItem, 'segmentIndex' | 'memberName'>;

function item(
  base: ItemBase,
  path: string,
  value: FieldValue | undefined,
  confidence: number | null,
  locations: FieldLocation[] | undefined,
): ExtractedItem {
  return {
    key: `${base.segmentIndex}:${path}`,
    path,
    segmentIndex: base.segmentIndex,
    memberName: base.memberName,
    display: value === undefined ? formatValue(null) : formatValue(value),
    confidence,
    notFound: isNotFound(value),
    locations: locations ?? [],
  };
}

/** A field, or one item per element of a non-empty list field (`field[i]`, 0-based, with the list's confidence). */
function fieldItems(base: ItemBase, path: string, f: NormalizedField | undefined): ExtractedItem[] {
  if (!f) return [item(base, path, undefined, null, undefined)];
  if (Array.isArray(f.value) && f.value.length > 0) {
    return f.value.map((x, i) => item(base, `${path}[${i}]`, [x] as FieldValue, f.confidence, f.itemLocations?.[i]));
  }
  // An empty list is a not-found value; it stays one item so it is neither drawn nor counted.
  return [item(base, path, f.value, f.confidence, Array.isArray(f.value) ? undefined : f.locations)];
}

function tableItems(base: ItemBase, table: NormalizedTable): ExtractedItem[] {
  return table.rows.flatMap((row, i) =>
    table.columns.map((col) => {
      const cell = row[col];
      return item(base, `${table.name}[${i}].${col}`, cell?.value, cell?.confidence ?? null, cell?.locations);
    }),
  );
}

function previewRowItems(base: ItemBase, row: PreviewRow, segment: NormalizedSegment): ExtractedItem[] {
  if (row.kind === 'group') return row.children.flatMap((c) => previewRowItems(base, c, segment));
  if (row.kind === 'table') {
    const table = row.found ? row.table : segment.tables.find((t) => t.name === row.path);
    return table ? tableItems(base, table) : [];
  }
  return fieldItems(
    base,
    row.path,
    segment.fields.find((f) => f.path === row.path),
  );
}

/** The Extracted_Items of a result in Result_View order, from `MATCH` segments only (Req 8.3, 8.4, 9.9). */
export function extractedItems(
  result: NormalizedResult,
  opts: { preview?: PreviewView; snapshot?: SetRunSnapshot } = {},
): AnnotationModel {
  const all = resultSegments(result);
  const isSet = isSetResult(all, opts.snapshot);
  const baseOf = (s: NormalizedSegment): ItemBase => ({
    segmentIndex: s.index,
    memberName: memberNameOf(s, isSet, opts.snapshot),
  });
  const items = opts.preview
    ? opts.preview.segments
        .filter(({ segment }) => segment.status === 'MATCH')
        .flatMap(({ segment, rows }) => rows.flatMap((r) => previewRowItems(baseOf(segment), r, segment)))
    : all
        .filter((s) => s.status === 'MATCH')
        .flatMap((s) => {
          const base = baseOf(s);
          return [
            ...s.fields.flatMap((f) => fieldItems(base, f.path, f)),
            ...s.tables.flatMap((t) => tableItems(base, t)),
          ];
        });
  return { items, isSet };
}

const inRange = (page: number, pageCount: number): boolean => page >= 1 && page <= pageCount;

/** One Annotation per (found item, location) on `page`; none when `page` is outside 1..pageCount (Req 6.1, 9.7). */
export function annotationsForPage(model: AnnotationModel, page: number, pageCount: number): Annotation[] {
  if (!inRange(page, pageCount)) return [];
  return model.items.flatMap((it) =>
    it.notFound
      ? []
      : it.locations.flatMap((location, i) =>
          location.page === page ? [{ key: `${it.key}#${i}`, item: it, location }] : [],
        ),
  );
}

const isLocated = (it: ExtractedItem, pageCount: number): boolean =>
  it.locations.some((l) => inRange(l.page, pageCount));

/** Found items with no location on any page 1..pageCount, in model order (Req 9.1, 9.6-9.8). */
export function unlocatedItems(model: AnnotationModel, pageCount: number): ExtractedItem[] {
  return model.items.filter((it) => !it.notFound && !isLocated(it, pageCount));
}

/** 'none': no found items; 'unavailable': found items but none located; 'available': at least one located. */
export function availability(model: AnnotationModel, pageCount: number): 'none' | 'unavailable' | 'available' {
  const found = model.items.filter((it) => !it.notFound);
  if (found.length === 0) return 'none';
  return found.some((it) => isLocated(it, pageCount)) ? 'available' : 'unavailable';
}

/** Labels carry the member name only on a set result's page with annotations from two or more segments (Req 8.5, 8.6). */
export function labelsWithMember(model: AnnotationModel, annotations: Annotation[]): boolean {
  return model.isSet && new Set(annotations.map((a) => a.item.segmentIndex)).size >= 2;
}

/** Full Annotation_Label (Req 6.4, 6.5, 8.5-8.7). */
export function annotationLabel(a: Annotation, opts: { showValues: boolean; withMember: boolean }): string {
  const prefix = opts.withMember && a.item.memberName !== null ? `${a.item.memberName} / ` : '';
  return opts.showValues ? `${prefix}${a.item.path}: ${a.item.display}` : `${prefix}${a.item.path}`;
}

/** The first 40 characters followed by an ellipsis when the label is longer (Req 6.6). */
export function visibleLabel(full: string): string {
  const chars = Array.from(full);
  return chars.length <= LABEL_VISIBLE_CHARS ? full : `${chars.slice(0, LABEL_VISIBLE_CHARS).join('')}…`;
}

/** Full label, confidence as a whole-number percent or "confidence unavailable", then "Low confidence" (Req 6.7, 6.12). */
export function annotationAccessibleName(a: Annotation, full: string, threshold: number): string {
  const c = a.item.confidence;
  const conf = c === null ? 'confidence unavailable' : `confidence ${Math.round(c * 100)}%`;
  return `${full}, ${conf}${isLow(c, threshold) ? ', Low confidence' : ''}`;
}

/** Text and capped path list of the Unlocated_Items (Req 9.1, 9.2). */
export function unlocatedSummary(items: ExtractedItem[]): { text: string; paths: string[]; more: number } {
  const n = items.length;
  return {
    text: `${n} extracted ${n === 1 ? 'field has' : 'fields have'} no location on the page`,
    paths: items.slice(0, UNLOCATED_LIST_MAX).map((it) => it.path),
    more: Math.max(0, n - UNLOCATED_LIST_MAX),
  };
}
